/* global google */
import { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MapPin, Search, RotateCcw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import {
  setTemporaryCoordinates,
  resetTemporaryCoordinates
} from '../../slices/locationSlice';

/**
 * ChangeLocation - Location search and temporary location management
 * Designed to be used inside AccountSettingsModal
 */
const ChangeLocation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const temporaryLocation = useSelector((state) => state.location.temporaryCoordinates);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!inputRef.current || !window.google) return;

      const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['(regions)'],
      });

      autocomplete.addListener('place_changed', async () => {
        setIsLoading(true);
        setConfirmationMessage('');

        try {
          const place = autocomplete.getPlace();

          if (!place.geometry) {
            setConfirmationMessage(t('placeDetailsNotFound', 'Place details not found'));
            setMessageType('error');
            return;
          }

          const location = place.geometry.location;
          const cityName = place.formatted_address;

          await new Promise((resolve) => setTimeout(resolve, 1000));

          dispatch(
            setTemporaryCoordinates({
              coordinates: [location.lng(), location.lat()],
              tempLocationName: cityName,
            })
          );

          if (inputRef.current) {
            inputRef.current.value = '';
            setTimeout(() => {
              if (inputRef.current) inputRef.current.value = cityName;
            }, 10);
          }

          setConfirmationMessage(t('temporaryLocationSet', 'Location set to') + ` ${cityName}`);
          setMessageType('success');
        } catch (error) {
          console.error('Error setting location:', error);
          setConfirmationMessage(t('locationSetError', 'Error setting location'));
          setMessageType('error');
        } finally {
          setIsLoading(false);
        }
      });
    };

    if (window.google) {
      initializeAutocomplete();
    } else {
      window.addEventListener('load', initializeAutocomplete);
      return () => window.removeEventListener('load', initializeAutocomplete);
    }
  }, [dispatch, t]);

  const handleReset = async () => {
    setIsLoading(true);
    setConfirmationMessage('');

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      dispatch(resetTemporaryCoordinates());

      if (inputRef.current) {
        inputRef.current.value = '';
      }

      setConfirmationMessage(t('locationResetSuccess', 'Location reset to your current location'));
      setMessageType('success');
    } catch (error) {
      console.error('Error resetting location:', error);
      setConfirmationMessage(t('locationResetError', 'Error resetting location'));
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Description */}
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900">
          {t('changeLocationTitle', 'Change Location')}
        </h3>
        <p className="text-sm text-gray-600">
          {t('changeLocationDescription', 'Set a temporary location to view notifications from a different area.')}
        </p>
      </div>

      {/* Current Temporary Location */}
      {temporaryLocation?.tempLocationName && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
          <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">
              {t('current_temp_location', 'Current Temporary Location')}
            </p>
            <p className="text-sm font-semibold text-blue-900 truncate">
              {temporaryLocation.tempLocationName}
            </p>
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Search className="w-5 h-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder={t('searchLocationPlaceholder', 'Search for a city...')}
          className="w-full pl-12 pr-4 py-3.5 text-base border-2 border-gray-200 rounded-xl
                     bg-white text-gray-900 placeholder-gray-400
                     focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
                     transition-all duration-200"
        />
      </div>

      {/* Reset Button */}
      <button
        onClick={handleReset}
        disabled={!temporaryLocation || isLoading}
        className={`
          w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl
          font-semibold transition-all duration-200
          ${temporaryLocation && !isLoading
            ? 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }
        `}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('updating', 'Updating...')}</span>
          </>
        ) : (
          <>
            <RotateCcw className="w-5 h-5" />
            <span>{t('resetLocationButton', 'Reset to Current Location')}</span>
          </>
        )}
      </button>

      {/* Confirmation Message */}
      {confirmationMessage && (
        <div className={`
          flex items-center gap-3 p-4 rounded-xl
          ${messageType === 'success' ? 'bg-green-50' : 'bg-red-50'}
        `}>
          {messageType === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
          <p className={`text-sm font-medium ${messageType === 'success' ? 'text-green-800' : 'text-red-800'}`}>
            {confirmationMessage}
          </p>
        </div>
      )}
    </div>
  );
};

export default ChangeLocation;
